import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Heart, Sparkles, ArrowRight, Crown } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { ListingCard, ListingCardSkeleton } from "../components/ListingCard";
import { Progress } from "../components/ui/progress";

const StatCard = ({ icon: Icon, value, label, testid }) => (
  <div className="rounded-3xl border border-border bg-white p-6 shadow-sm" data-testid={testid}>
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brandblue-light text-brandblue">
      <Icon size={20} />
    </div>
    <div className="mt-4 font-display text-3xl font-extrabold text-navy">{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);

const Dashboard = () => {
  const { t } = useT();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api("/dashboard")
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center text-slate-500">{t("common.error")}</div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="dash-welcome">
        {t("dash.welcome", { name: user?.name || "" })}
      </h1>

      {/* Premium card — gold-accented for active premium; upgrade CTA for free */}
      {user?.is_premium ? (
        <Link
          to="/premium"
          data-testid="dash-premium-active-card"
          className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-amber-300/60 bg-gradient-to-br from-amber-50/50 via-white to-white p-5 shadow-sm transition hover:border-amber-400/80 hover:shadow-[0_10px_30px_-15px_rgba(11,27,61,0.35),0_0_0_1px_rgba(251,191,36,0.35)]"
        >
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-navy text-amber-300 ring-1 ring-amber-300/40">
              <Crown size={20} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-lg font-extrabold text-navy">{t("dash.premium.title")}</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-navy px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                  <Sparkles size={10} /> {t("premium.pill.active")}
                </span>
              </div>
              <p className="text-sm text-slate-600">{t("dash.premium.sub")}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-amber-300">
            {t("dash.premium.cta")} <ArrowRight size={14} />
          </span>
        </Link>
      ) : (
        <Link
          to="/premium"
          data-testid="dash-premium-upsell-card"
          className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-amber-300/60 bg-gradient-to-br from-amber-50/50 via-white to-white p-5 shadow-sm transition hover:border-amber-400/80 hover:shadow-[0_10px_30px_-15px_rgba(11,27,61,0.35),0_0_0_1px_rgba(251,191,36,0.35)]"
        >
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 ring-1 ring-amber-300/40">
              <Sparkles size={20} />
            </span>
            <div>
              <div className="font-display text-lg font-extrabold text-navy">{t("dash.upsell.title")}</div>
              <p className="text-sm text-slate-600">{t("dash.upsell.sub")}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-amber-300">
            {t("dash.upsell.cta")} <ArrowRight size={14} />
          </span>
        </Link>
      )}

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        {data === null ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-3xl bg-slate-100" />
          ))
        ) : (
          <>
            <StatCard icon={BellRing} value={data.alerts_active} label={t("dash.stat.alerts")} testid="stat-alerts" />
            <StatCard icon={Heart} value={data.saved_count} label={t("dash.stat.saved")} testid="stat-saved" />
            <StatCard icon={Sparkles} value={data.matches_today_count} label={t("dash.stat.matches")} testid="stat-matches" />
          </>
        )}
      </div>

      {/* Profile completeness */}
      {data?.profile_score !== undefined && (
        <div className="mt-6 rounded-3xl border border-border bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-navy">{t("dash.profile.title")}</h2>
            <span className="font-display text-lg font-extrabold text-brandblue">{data.profile_score}%</span>
          </div>
          <Progress value={data.profile_score} className="mt-4 h-2.5" />
          {(data.profile_missing_keys || data.profile_missing)?.length > 0 && (
            <p className="mt-3 text-sm text-slate-500">
              {t("dash.profile.missing")}{" "}
              {(data.profile_missing_keys
                ? data.profile_missing_keys.map((k) => t(`pf.${k}`))
                : data.profile_missing
              ).slice(0, 5).join(", ")}
            </p>
          )}
        </div>
      )}

      {/* Matches */}
      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-display text-2xl font-extrabold text-navy">{t("dash.matches.title")}</h2>
        <Link to="/woningen" className="flex items-center gap-1 text-sm font-semibold text-brandblue">
          {t("common.viewAll")} <ArrowRight size={15} />
        </Link>
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data === null ? (
          Array.from({ length: 3 }).map((_, i) => <ListingCardSkeleton key={i} />)
        ) : data.matches_today?.length ? (
          data.matches_today.map((l) => <ListingCard key={l.id} listing={l} />)
        ) : (
          <div className="col-span-full rounded-3xl border border-dashed border-border p-12 text-center text-slate-500">
            {t("dash.empty.matches")}
          </div>
        )}
      </div>

      {/* Recently viewed */}
      {data?.recently_viewed?.length > 0 && (
        <>
          <h2 className="mt-10 font-display text-2xl font-extrabold text-navy">{t("dash.recent.title")}</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.recently_viewed.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
